import React, {useEffect} from 'react';
import {createStructuredSelector} from 'reselect';
import {useDispatch, useSelector} from 'react-redux';
import {navigate} from 'gatsby';
import {
    getAddedMaxCoverMembers,
    getCreateQuoteSuccess,
    getIsOnInitPage,
    getQuotePageCoverList,
    getWaitingForCreateQuote,
} from '@om/redux/selectors/easiplusFuneralPlan/quote';
import {
    sendQuoteCreateCancel,
    setNewCoverDefault,
    setNewCoverExtendedFamily,
    setNewCoverMyself,
    setNewCoverParents,
    setSideDrawerOpen,
} from '@om/redux/actions/easiplusFuneralPlan/quote';
import {EFP_ADD_COVER_DRAWERS, PAGE_INDICES} from '@om/redux/actions/easiplusFuneralPlan/types';
import {membersUpdatedLivesCovered} from '@om/redux/actions/easiplusFuneralPlan/livesCovered';
import {navigateIndex} from '@om/redux/actions/easiplusFuneralPlan/routing';
import {getActivePageIndex} from '@om/redux/selectors/easiplusFuneralPlan/routing';
import {
    OmApplicationCoverCategories,
    OmApplicationCoverCategoryCard,
    OmIcon,
    OmEfpQuotePage,
    OmModalLoader,
} from '@om/component-library-react';
import OmButton from '../../../components/forms/OmButton';
import EfpFamilyMemberCardGroup from './EfpFamilyMemberCardGroup';
import QuoteSummaryCard from './QuoteSummaryCard';
import QuoteModals from './quoteModals/QuoteModals';

import { getRequestId } from '@om/redux/selectors/easiplusFuneralPlan/sessionCamVariables';
import { useSessionCamVariables } from '../../../hooks/useSessionCamVariables';

import './QuotePage.scss';
import { toggleWhatsAppBubbleWidget } from '@om/redux/middleware/rulesEngine/easiplusFuneralPlan/quote';
import { Helmet } from 'react-helmet';

const InitCoverView = () => {
    const dispatch = useDispatch();

    const openDirectFamilySideDrawer = () => {
        toggleWhatsAppBubbleWidget(false);
        dispatch(setNewCoverMyself());
        dispatch(setSideDrawerOpen(EFP_ADD_COVER_DRAWERS.DIRECT_FAMILY));
    };
    const openParentsSideDrawer = () => {
        toggleWhatsAppBubbleWidget(false);
        dispatch(setNewCoverParents());
        dispatch(setSideDrawerOpen(EFP_ADD_COVER_DRAWERS.PARENTS_AND_IN_LAWS));
    };
    const openExtendedFamilySideDrawer = () => {
        toggleWhatsAppBubbleWidget(false);
        dispatch(setNewCoverExtendedFamily());
        dispatch(setSideDrawerOpen(EFP_ADD_COVER_DRAWERS.EXTENDED_FAMILY));
    };

    return (
        <div className="is-init-page">
            <OmApplicationCoverCategories>
                <OmApplicationCoverCategoryCard slot="first">
                    <OmIcon
                        slot="icon"
                        imageUrl="https://eu-images.contentstack.com/v3/assets/blt0554f48052bb4620/blt0f93690bb13df2a7/5f61ddd6046a1b546bb1592a/direct-family-gradient.svg"
                    />
                    <span slot="title">
                        Me &amp; My Direct Family
                    </span>
                    <span slot="button">
                        <OmButton
                            text="Add Cover"
                            type="secondary"
                            size="small"
                            onClick={() => openDirectFamilySideDrawer()}
                        />
                    </span>
                </OmApplicationCoverCategoryCard>

                <OmApplicationCoverCategoryCard slot="second">
                    <OmIcon
                        slot="icon"
                        imageUrl="https://eu-images.contentstack.com/v3/assets/blt0554f48052bb4620/blteaa4e52be1a62cee/5f61ddd625f7a73d06685672/extended-family-gradient.svg"
                    />
                    <span slot="title">
                        My Parents &amp; In-laws
                    </span>
                    <span slot="button">
                        <OmButton
                            text="Add Cover"
                            type="secondary"
                            size="small"
                            onClick={() => openParentsSideDrawer()}
                        />
                    </span>
                </OmApplicationCoverCategoryCard>

                <OmApplicationCoverCategoryCard slot="third">
                    <OmIcon
                        slot="icon"
                        imageUrl="https://eu-images.contentstack.com/v3/assets/blt0554f48052bb4620/bltc0d8ac0985724060/5f61ddd6ea082953944f735d/parents-and-inlaws-gradient.svg"
                    />
                    <span slot="title">
                        My Extended Family
                    </span>
                    <span slot="button">
                        <OmButton
                            text="Add Cover"
                            type="secondary"
                            size="small"
                            onClick={() => openExtendedFamilySideDrawer()}
                        />
                    </span>
                </OmApplicationCoverCategoryCard>
            </OmApplicationCoverCategories>
        </div>
    );
};

const MoreCoverView = () => {
    const dispatch = useDispatch();
    const {addedMaxCoverMembers, quotePageCoverList} = useSelector(
        createStructuredSelector({
            addedMaxCoverMembers: getAddedMaxCoverMembers,
            quotePageCoverList: getQuotePageCoverList,
        })
    );

    const openLivesCoveredSideDrawer = () => {
        if (addedMaxCoverMembers) {
            return;
        }

        dispatch(setNewCoverDefault());
        dispatch(setSideDrawerOpen(EFP_ADD_COVER_DRAWERS.LIVES_COVERED));
    };

    return (
        <div>
            <div className="is-not-init-page">
                <div className="family-member-card-group-wrapper">
                    <div className="quote-heading-wrapper">
                        <h4 className="light no-top-margin">
                            Here’s how much it will cost
                        </h4>
                    </div>

                    {quotePageCoverList.map((displayCover, keyIndex) =>
                        <EfpFamilyMemberCardGroup
                            key={keyIndex}
                            displayList={displayCover.displayList}
                            isCollapsed={displayCover.isCollapsed}
                            category={displayCover.category}
                            headingText={displayCover.headingText}
                            expandButtonText={displayCover.expandButtonText}
                        />
                    )}
                </div>
            </div>

            <div className="add-more-cover-wrapper">
                <h5 className="medium">Would you like to cover more people?</h5>
                <div className="button-wrapper">
                    <OmButton
                        text="ADD COVER +"
                        type="secondary"
                        size="small"
                        actionType={addedMaxCoverMembers}
                        onClick={() => openLivesCoveredSideDrawer()}
                    />
                </div>
            </div>
        </div>
    );
};

const QuotePage = ({slot}) => {
    const dispatch = useDispatch();
    const {
        isOnInitPage,
        waitingForCreateQuote,
        createQuoteSuccess,
        requestId,
        activePageIndex,
    } = useSelector(
        createStructuredSelector({
            isOnInitPage: getIsOnInitPage,
            waitingForCreateQuote: getWaitingForCreateQuote,
            createQuoteSuccess: getCreateQuoteSuccess,
            requestId: getRequestId,
            activePageIndex: getActivePageIndex,
        })
    );

    useEffect(() => {
        if (createQuoteSuccess) {
            dispatch(membersUpdatedLivesCovered());
            dispatch(navigateIndex(PAGE_INDICES.REPLACEMENT_POLICY));
            dispatch(sendQuoteCreateCancel());
            navigate('/easiplus-funeral-plan/replacement-policy');
        }
    }, [createQuoteSuccess, activePageIndex, dispatch]);

    useSessionCamVariables(requestId);

    return (
        <>
            <Helmet>
                <script defer
                    src="https://widget.rather.chat/chat-widget.js"
                    data-company="U2eKcCRcjnzF"
                    data-default-country-code="ZA"
                    data-channel="9167f13a-0c75-4b09-bb3f-a4369c81d802"
                ></script>
            </Helmet>
            <OmEfpQuotePage slot={slot} className={'efp-quote-page'}>
                <div slot="side-drawers">
                    <QuoteModals />
                </div>

                <div slot="quote-page">
                    {isOnInitPage
                        ? <InitCoverView />
                        : <MoreCoverView />
                    }

                    <OmModalLoader
                        verticalAlignMiddle
                        loaderText="Submitting your quote"
                        open={waitingForCreateQuote}
                    />
                </div>

                <div slot="quote-summary-card">
                    <div>
                        {!isOnInitPage && <QuoteSummaryCard name="quote-card" />}
                    </div>
                </div>

                <div slot="quote-summary-card-sticky">
                    <div>
                        {!isOnInitPage && <QuoteSummaryCard isSticky name="quote-card-sticky" />}
                    </div>
                </div>
            </OmEfpQuotePage>
        </>
    );
};

export default QuotePage;
